import React from "react";
import RiskAssessmentComponent from "./riskassessment/RiskAssessment";

/**
 * RiskAssessmentTab — dedicated top-level surface for the Investor Risk
 * Profile assessment + results.
 *
 * Previously this lived inside `ProfileTab` alongside the static
 * Financial Profile Summary. Promoted to its own sidebar entry on
 * 2026-06-17 because: (a) it's a substantial feature that warrants
 * top-level discoverability, (b) the student mental model of "my risk
 * profile" is distinct from "my financial profile summary," and (c) it
 * sets up the future stock-to-profile migration feature where research
 * picks will flow into this surface.
 *
 * State lifting is unchanged — `riskAssessmentData` and
 * `setRiskAssessmentData` still live in FinancialDashboard and are
 * persisted to Firestore via the existing save flow.
 *
 * ARC-59: added `loadError`. When the read that should have produced
 * `riskAssessmentData` failed, or returned a shape we could not parse,
 * this tab must NOT mount the assessment. RiskAssessment seeds its local
 * state from the prop once and then mirrors that state straight back to
 * the parent, so mounting it with nothing loaded is precisely what
 * manufactured the empty `{ answers: {}, results: null }` that replaced a
 * completed profile on 2026-08-18. An error panel renders instead: there
 * is no blank assessment in the save path because there is no assessment
 * mounted at all.
 */
const RiskAssessmentTab = ({
  questions,
  calculateResults,
  QuestionSlider,
  ProgressBar,
  CryptoReportView,
  logo,
  riskAssessmentData,
  setRiskAssessmentData,
  loadError,
  hasSavedAssessment,
  onRetakeAcknowledged,
}) => {
  // ARC-59: fail closed BEFORE the readiness check, so a load failure can
  // never be mistaken for "still loading" and quietly resolve into a
  // mounted, blank assessment.
  if (loadError) {
    return (
      <div className="surface-xl p-8 text-center" role="alert">
        <h2 className="headline text-xl mb-3">Risk profile unavailable</h2>
        <p className="text-arc-muted text-sm max-w-xl mx-auto">{loadError}</p>
      </div>
    );
  }

  // Defensive: if any of the required props haven't loaded yet, show a
  // brief skeleton instead of crashing. This matches the conditional
  // render pattern that used to live in ProfileTab.
  const ready =
    questions && calculateResults && QuestionSlider && ProgressBar && CryptoReportView;

  if (!ready) {
    return (
      <div className="surface-xl p-8 text-center">
        <p className="text-arc-muted text-sm">Loading Risk Profile…</p>
      </div>
    );
  }

  return (
    <RiskAssessmentComponent
      questions={questions}
      calculateResults={calculateResults}
      QuestionSlider={QuestionSlider}
      ProgressBar={ProgressBar}
      CryptoReportView={CryptoReportView}
      logo={logo}
      riskAssessmentData={riskAssessmentData}
      setRiskAssessmentData={setRiskAssessmentData}
      hasSavedAssessment={hasSavedAssessment}
      onRetakeAcknowledged={onRetakeAcknowledged}
    />
  );
};

export default RiskAssessmentTab;
